import { last } from "lodash-es";

import {
  CaseHistoryDeserialized,
  CaseHistoryEvent,
  GroupableEventType,
  GroupableRootEventType,
  HistoryEntry,
  HistoryEntrySingle,
  PinnedGroupableEventType,
  SingleEventType,
} from "src/cases/queries/casesTypes";
import { logger } from "src/utils/logger";

const GROUP_ROOT_EVENT_TYPES: readonly (
  | GroupableRootEventType
  | PinnedGroupableEventType
)[] = [
  "case_creation_flow_initiated",
  "action_triggered",
  "pinned_entities_updated",
  "pinned_events_updated",
] as const;

// Event types that can be grouped together
const GROUPABLE_EVENT_TYPES: GroupableEventType[] = [
  "case_creation_flow_initiated",
  "action_triggered",
  "action_completed",
  "action_failed",
] as const;

const PINNED_GROUPABLE_EVENT_TYPES: PinnedGroupableEventType[] = [
  "pinned_entities_updated",
  "pinned_events_updated",
] as const;
const PINNED_GROUP_TIME_WINDOW = 1 * 60 * 1000;

const isGroupableEvent = (
  event: CaseHistoryDeserialized["data"][number],
): event is CaseHistoryEvent<GroupableEventType> => {
  return GROUPABLE_EVENT_TYPES.includes(
    event.event_data.event_type as GroupableEventType,
  );
};

const isPinnedGroupableEvent = (
  event: CaseHistoryDeserialized["data"][number],
): event is CaseHistoryEvent<PinnedGroupableEventType> => {
  return PINNED_GROUPABLE_EVENT_TYPES.includes(
    event.event_data.event_type as PinnedGroupableEventType,
  );
};

const isSingleEvent = (
  event: CaseHistoryDeserialized["data"][number],
): event is CaseHistoryEvent<SingleEventType> => {
  return !isGroupableEvent(event) && !isPinnedGroupableEvent(event);
};

const getRunningActionId = (
  event: CaseHistoryEvent<GroupableEventType>,
): string | null | undefined => {
  return event.event_data.running_action_id;
};

export const groupHistoryEvents = (
  events: CaseHistoryDeserialized["data"],
): HistoryEntry[] => {
  const result: HistoryEntry[] = [];

  const actionGroups = groupActionRelatedEvents(events);

  // Process events in original order (newest first from backend)
  for (const event of events) {
    /**
     * Single events are not grouped
     */
    if (isSingleEvent(event)) {
      result.push(buildSingleHistoryEntry(event));
    } else if (isGroupableEvent(event)) {
      /**
       * Action related events are grouped together
       */
      const runningActionId = getRunningActionId(event);

      if (!runningActionId) {
        // Skip if event has no running action id
        continue;
      }

      if (
        event.event_data.event_type === "action_triggered" ||
        event.event_data.event_type === "case_creation_flow_initiated"
      ) {
        // We found the root event of the group
        const events = actionGroups.get(runningActionId) ?? [];

        const sortedEvents = sortGroupedActionEvents(events);

        const group = buildGroupHistoryEntry(sortedEvents);
        if (group) {
          result.push(group);
          // Delete the group from the map
          actionGroups.delete(runningActionId);
        }
      }
    } else if (isPinnedGroupableEvent(event)) {
      const lastEntry = last(result);

      // If no last entry or last entry is not a group
      // Just create another group entry for the new event
      if (!lastEntry || lastEntry.type !== "group") {
        const group = buildGroupHistoryEntry([event]);
        if (group) {
          result.push(group);
        }
        continue;
      }

      // If last entry event type is different
      // Just create another group entry for the new event
      if (lastEntry.event_type !== event.event_data.event_type) {
        const group = buildGroupHistoryEntry([event]);
        if (group) {
          result.push(group);
        }
        continue;
      }

      const groupLastEvent =
        lastEntry.event_type === "pinned_entities_updated"
          ? last(
              lastEntry.events as CaseHistoryEvent<"pinned_entities_updated">[],
            )
          : last(
              lastEntry.events as CaseHistoryEvent<"pinned_events_updated">[],
            );

      if (!groupLastEvent) {
        // This should never happen
        logger.error("No last event found for pinned groupable event");
        continue;
      }

      const isSameBlock =
        groupLastEvent?.event_data.block_key === event.event_data.block_key;
      const isSameUser =
        groupLastEvent?.event_data.updated_by_user_id ===
        event.event_data.updated_by_user_id;
      const isWithinTimeWindow =
        Math.abs(
          new Date(groupLastEvent.timestamp).getTime() -
            new Date(event.timestamp).getTime(),
        ) <= PINNED_GROUP_TIME_WINDOW;

      if (isSameBlock && isSameUser && isWithinTimeWindow) {
        if (lastEntry.event_type === "pinned_entities_updated") {
          lastEntry.events.push(
            event as CaseHistoryEvent<"pinned_entities_updated">,
          );
        } else {
          lastEntry.events.push(
            event as CaseHistoryEvent<"pinned_events_updated">,
          );
        }
        continue;
      }

      const group = buildGroupHistoryEntry([event]);
      if (group) {
        result.push(group);
      }
      continue;
    }
  }

  return result;
};

const buildGroupHistoryEntry = (
  events: CaseHistoryEvent<GroupableEventType | PinnedGroupableEventType>[],
): HistoryEntry | undefined => {
  const lastEvent = last(events);

  if (!lastEvent) {
    logger.error("No last event found for groupable event");
    return;
  }

  if (
    !GROUP_ROOT_EVENT_TYPES.includes(
      lastEvent.event_data.event_type as
        | GroupableRootEventType
        | PinnedGroupableEventType,
    )
  ) {
    logger.error("Last event is not a group root event type");
    return;
  }

  const eventType = lastEvent.event_data.event_type;

  if (eventType === "pinned_entities_updated") {
    return {
      type: "group" as const,
      event_type: "pinned_entities_updated",
      id: lastEvent.id,
      events: events as CaseHistoryEvent<"pinned_entities_updated">[],
    };
  }

  if (eventType === "pinned_events_updated") {
    return {
      type: "group" as const,
      event_type: "pinned_events_updated",
      id: lastEvent.id,
      events: events as CaseHistoryEvent<"pinned_events_updated">[],
    };
  }

  return {
    type: "group" as const,
    event_type: eventType as GroupableRootEventType,
    id: lastEvent.id,
    events: events as CaseHistoryEvent<GroupableEventType>[],
  };
};

const buildSingleHistoryEntry = (
  event: CaseHistoryEvent<SingleEventType>,
): HistoryEntrySingle => {
  return {
    id: event.id,
    type: "single" as const,
    event_type: event.event_data.event_type,
    event,
  };
};

const groupActionRelatedEvents = (
  events: CaseHistoryDeserialized["data"],
): Map<string, CaseHistoryEvent<GroupableEventType>[]> => {
  const groups = new Map<string, CaseHistoryEvent<GroupableEventType>[]>();

  for (const event of events) {
    if (!isGroupableEvent(event)) {
      continue;
    }

    const runningActionId = getRunningActionId(event);

    if (!runningActionId) {
      // Skip if event has no running action id
      continue;
    }

    const existingGroup = groups.get(runningActionId) ?? [];
    groups.set(runningActionId, [...existingGroup, event]);
  }

  return groups;
};

/**
 * We just move the first appeared trigger to the end of the group
 * to ensure the root event is always last, there rest of events appear
 * in the order they came from the backend.
 */
const sortGroupedActionEvents = (
  events: CaseHistoryEvent<GroupableEventType>[],
): CaseHistoryEvent<GroupableEventType>[] => {
  const firstTriggerIndex = events.findLastIndex(
    (event) =>
      event.event_data.event_type === "action_triggered" ||
      event.event_data.event_type === "case_creation_flow_initiated",
  );

  if (firstTriggerIndex === -1) {
    // Fallback to original order
    return events;
  }

  // Move found index to end of array
  const firstTrigger = events[firstTriggerIndex];

  return [
    ...events.slice(0, firstTriggerIndex),
    ...events.slice(firstTriggerIndex + 1),
    firstTrigger,
  ];
};
